/**
 * petImages — Pet Companion 画像 URL ヘルパー
 *
 * 関連 spec: .company/departments/ux-design/2026-05-04-pet-companion-design-spec.md
 * 関連 task : 2026-05-05 designer → sys-dev (PNG 化 + Storage 統合)
 *
 * 設計方針:
 *  - Supabase Storage `pet-companions` バケット (public read) から PNG を配信する
 *  - パス命名規約: `{species}/{stage}-{emotion}.png`
 *      例: `kitsune/mature-warm.png`
 *  - DB 上の runtime emotion (calm/joy/sad/...) と PNG ファイル側 emotion
 *    (warm/determined/free/...) は **別語彙** である:
 *      - DB emotion = 24h 行動から推定される動的状態
 *      - PNG emotion = 種族ごとに「らしさ」を表現するための表情バリエーション
 *  - MVP では各 species につき "default emotion" の mature PNG のみ存在する。
 *    今後 stage × emotion 別バリエーションが追加されたら resolveEmotion の
 *    マッピングテーブルを拡張する。
 *  - PNG が未生成のステージ (egg/hatch/cocoon 等) は呼び出し側が SVG で描画する。
 *    `getPetImageUrl` は URL を組み立てるだけで、404 時の fallback は呼び出し側
 *    (`PetAvatar.tsx` の <img onError>) で処理する。
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? ''
const BUCKET = 'pet-companions'

/**
 * 各 species の "default emotion" — PNG のファイル名に使われる感情ラベル。
 * archetype 確定時に固定 (designer 部 2026-05-05). 種族の「らしさ」を表す根源的な感情。
 */
export const defaultEmotionBySpecies: Record<string, string> = {
  // 既存 8 種
  kitsune: 'warm',
  lupus: 'determined',
  avian: 'free',
  lutra: 'playful',
  moss: 'calm',
  lumen: 'hopeful',
  cervus: 'reverent',
  baku: 'dreamy',
  // 新規 19 種 (Phase B 2026-05-05)
  papilio: 'delicate',
  delphinus: 'playful',
  psittacus: 'curious',
  strix: 'wise',
  serpens: 'calm',
  gekko: 'observant',
  canis: 'loyal',
  equus: 'dignified',
  erinaceus: 'shy',
  panda: 'balanced',
  cricetus: 'timid',
  testudo: 'steady',
  bradypus: 'content',
  ovis: 'gentle',
  pingu: 'sociable',
  cavia: 'bashful',
  hydrochoerus: 'zen',
  rana: 'quiet',
  crocodylus: 'stoic',
}

/**
 * runtime emotion (DB の calm/joy/sad/...) を PNG 側の emotion ラベルに解決する。
 *
 * MVP 段階: 各 species は default emotion の PNG しか存在しないため、
 * 入力に関わらず常に default を返す。今後バリエーションが揃ったら以下のような
 * 多段マップに拡張する想定:
 *
 *   const map: Record<Species, Partial<Record<RuntimeEmotion, PngEmotion>>> = {
 *     kitsune: { joy: 'warm', curious: 'wonder', calm: 'warm', tired: 'tender', ... }
 *   }
 */
export function resolveEmotion(species: string, _requested?: string): string {
  return defaultEmotionBySpecies[species] ?? 'calm'
}

/**
 * Pet 画像の public URL を組み立てる。
 *
 * @param species - 内部 ID (kitsune / lupus / avian / lutra / moss / lumen / cervus / baku)
 * @param stage   - egg / hatch / juvenile / mature / sage
 * @param emotion - PNG ファイル名側の emotion (例: warm / determined / ...).
 *                  runtime emotion を渡したい場合は事前に `resolveEmotion` を通すこと。
 * @returns Supabase Storage の public URL. SUPABASE_URL 未設定時は空文字。
 */
export function getPetImageUrl(species: string, stage: string, emotion: string): string {
  if (!SUPABASE_URL) return ''
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${species}/${stage}-${emotion}.png`
}

/**
 * 画像 URL を生成しつつ runtime emotion を default に解決する糖衣。
 * PetAvatar など呼び出し側が runtime emotion をそのまま渡せるようにする。
 */
export function getPetImageUrlForRuntime(
  species: string,
  stage: string,
  runtimeEmotion?: string,
): string {
  return getPetImageUrl(species, stage, resolveEmotion(species, runtimeEmotion))
}

/**
 * 副軸 CA バリエーション URL を組み立てる (Phase E v13 共通色パレット).
 *
 * v13 (2026-05-06) で 4 副軸すべてが実 variant 化:
 *   - LH 温桃 / HH 蜂蜜金 / HL 霧銀 / LL 苔灰 — 全 27 種で色テーマ統一
 *
 * @param species - 内部 ID
 * @param ca      - "LH" / "HH" / "HL" / "LL" のいずれか. null はまだ CA 推定前 → default mature
 * @param fallbackStage - default fallback の stage (default: 'mature')
 * @param fallbackEmotion - default fallback の emotion (default: 種の default)
 */
export function getPetVariantUrl(
  species: string,
  ca: string | null,
  fallbackStage: string = 'mature',
  fallbackEmotion?: string,
): string {
  if (!SUPABASE_URL) return ''
  // null = CA 未推定 → default mature
  if (!ca) {
    return getPetImageUrl(species, fallbackStage, resolveEmotion(species, fallbackEmotion))
  }
  if (ca === 'LH' || ca === 'HH' || ca === 'HL' || ca === 'LL') {
    return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${species}/variant-${ca}.png`
  }
  // 未知の値は default
  return getPetImageUrl(species, fallbackStage, resolveEmotion(species, fallbackEmotion))
}
